import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Copy,
  Download,
  FileCode2,
  Image as ImageIcon,
  Printer,
} from "lucide-react";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { breadcrumbJsonLd, ldScript, pageHead, softwareAppJsonLd } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/aztec-code-generator";
const TITLE = "Aztec Code Generator — Create Aztec 2D Barcodes Online";
const DESC =
  "Free Aztec code generator. Encode boarding passes, transit tickets and structured data as a 2D Aztec barcode with adjustable EC and layer count.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "Aztec Code Generator", path: PATH },
];

const FAQ = [
  {
    q: "What Is an Aztec Code?",
    a: "Aztec is a 2D matrix barcode introduced in 1995 with a distinctive square 'bullseye' finder pattern in the centre. It encodes from a few bytes up to ~3067 ASCII characters, doesn't require a quiet zone (unlike QR), and is the symbology used for IATA mobile boarding passes and many European rail tickets.",
  },
  {
    q: "Compact vs Full-Range — What's the Difference?",
    a: "Compact Aztec uses a 2-ring bullseye and 1–4 layers (15×15 to 27×27 modules); good for small payloads up to ~110 characters. Full-range uses a 3-ring bullseye and 1–32 layers (19×19 to 151×151 modules) for longer data. Most modern scanners read both.",
  },
  {
    q: "What Error Correction Percentage Should I Use?",
    a: "The Aztec spec recommends 23% (the default). Bump to 36–50% for laminated cards, dirty surfaces or low-DPI thermal prints. Lower than 23% saves space but reduces tolerance to print defects and scanner noise.",
  },
  {
    q: "What Does 'Layers' Control?",
    a: "Each layer adds a square ring of modules around the central bullseye. More layers = bigger symbol and more data capacity. Auto lets bwip-js pick the smallest layer count that fits your data at the chosen EC level. Force a specific layer count to hit a fixed label size.",
  },
  {
    q: "When Should I Use Reader-Programming Aztec?",
    a: "Aztec supports a 'reader init' flag that tells barcode scanners to programme themselves from the symbol — used in IT deployment to push scanner configs. Don't enable it for normal data payloads.",
  },
  {
    q: "Is My Data Uploaded Anywhere?",
    a: "No. The barcode is rendered locally with the open-source bwip-js library. The text you encode and the files you download never leave your browser.",
  },
  {
    q: "How Do You Read an Aztec Code?",
    a: "Point a 2D imager or a phone app that supports Aztec at the symbol; it reads at any rotation because the decoder locates the central bullseye and its orientation marks first. Airport gates and ticket barriers use fixed imagers built for this. For a quick check at your desk, a phone or laptop camera is enough.",
  },
  {
    q: "Can an Aztec Code Be Decoded Online?",
    a: "Yes. The ReadBarcode barcode reader decodes Aztec in your browser from the camera or from an uploaded photo or screenshot. The image is not uploaded, so a ticket payload stays on your device. It is a quick way to check a code you created on this page before you print it.",
  },
];

type LayersChoice = "auto" | string; // "auto" or "1".."32"

// Practical cap for full-range Aztec at default EC (23%). bwip-js will report
// a precise encoder error if the data can't fit at the chosen layer / EC.
const MAX_CHARS = 3000;

function validate(data: string, compact: boolean): string | null {
  if (data.length === 0) return "Enter the data to encode.";
  if (data.length > MAX_CHARS)
    return `Data is ${data.length} chars — Aztec maximum is ~${MAX_CHARS} (full-range, alphanumeric).`;
  if (compact && data.length > 110)
    return "Compact Aztec is limited to about 110 characters. Turn off Compact for longer payloads.";
  return null;
}

function safeFilename(data: string): string {
  const cleaned = data
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `aztec-${cleaned || "code"}`;
}

function AztecCodeGeneratorPage() {
  const [data, setData] = useState("M1DOE/JOHN          EABC123 LHRJFKBA  0179 226Y012A0001 100");
  const [printOpen, setPrintOpen] = useState(false);
  const [eclevel, setEclevel] = useState(23);
  const [layers, setLayers] = useState<LayersChoice>("auto");
  const [moduleScale, setModuleScale] = useState(3);
  const [compact, setCompact] = useState(false);
  const [readerInit, setReaderInit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");
  const [symbolSize, setSymbolSize] = useState<{ w: number; h: number } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const validationErr = useMemo(() => validate(data, compact), [data, compact]);

  // Compact tops out at 4 layers; if compact is on with a higher layer choice,
  // clamp the actual rendering input.
  const effectiveLayers = useMemo(() => {
    if (layers === "auto") return null;
    const n = Number(layers);
    if (compact) return Math.min(n, 4);
    return Math.min(n, 32);
  }, [layers, compact]);

  useEffect(() => {
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      setSymbolSize(null);
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    loadBwip().then((bwipjs) => {
      if (cancelled) return;
      try {
        const opts: Record<string, unknown> & { bcid: string; text: string } = {
          bcid: compact ? "azteccodecompact" : "azteccode",
          text: data,
          scale: moduleScale,
          eclevel,
          backgroundcolor: "FFFFFF",
        };
        if (effectiveLayers !== null) opts.layers = effectiveLayers;
        if (readerInit) opts.readerinit = true;
        if (canvasRef.current) bwipjs.toCanvas(canvasRef.current, opts);
        const svg = bwipjs.toSVG(opts);
        setSvgMarkup(svg);
        const vb = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
        setSymbolSize(vb ? { w: Number(vb[1]), h: Number(vb[2]) } : null);
        setError(null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to render Aztec code.";
        setError(msg);
        setSvgMarkup("");
        setSymbolSize(null);
        const c = canvasRef.current;
        if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [data, eclevel, effectiveLayers, moduleScale, compact, readerInit, validationErr]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(data)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(data)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyData = async () => {
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Aztec Code Generator"
      description="Encode boarding passes, transit tickets and structured payloads as a 2D Aztec barcode. Tunable error correction and layer count, PNG and SVG output — fully client-side. A compact mode produces smaller symbols of 15 × 15 to 27 × 27 modules, and a reader-programming switch sets the flag used for scanner configuration codes."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the Aztec code generator"
            about={
              <>
                <p>
                  Aztec is a 2D matrix barcode that packs a few characters or up to ~3000 of them into a
                  square symbol with a central bullseye finder pattern. It scans without a quiet zone
                  (which is why it works so well on phone screens with thin status bars), tolerates
                  partial damage, and is the symbology behind IATA mobile boarding passes, Eurostar /
                  SNCF / Deutsche Bahn tickets, and many transit cards.
                </p>
                <p>
                  This generator renders Aztec entirely in your browser with the open-source bwip-js
                  library. Pick an error-correction percentage (the spec default is 23%), choose compact
                  vs full-range, force a layer count for a fixed label size, then download as SVG for
                  print or PNG for screen.
                </p>
              </>
            }
            whenToUse={[
              "Generating IATA BCBP mobile boarding-pass payloads for proofing.",
              "Issuing transit / rail tickets that need to scan from glossy phone screens.",
              "Marking small parts where Aztec's no-quiet-zone requirement saves real estate.",
              "Producing reader-config codes that re-programme handheld scanners.",
              "Embedding short structured data (gate, seat, member ID) on a printed pass.",
              "Replacing a damaged Aztec on an existing ticket / pass with a re-issue.",
            ]}
            howItWorks={[
              {
                title: "Paste your data",
                desc: "ASCII text, digits or Latin-1 binary up to ~3000 characters.",
              },
              {
                title: "Set the EC percentage",
                desc: "Stay at the 23% spec default for general use. Raise for harsher surfaces / lower-DPI printers.",
              },
              {
                title: "Pick compact or full-range",
                desc: "Compact for short payloads (~110 chars). Full-range for everything else.",
              },
              {
                title: "Optionally force layers",
                desc: "Auto picks the smallest symbol that fits; force a count to hit a label size.",
              },
              {
                title: "Download SVG or PNG",
                desc: "SVG scales without artefacts to any size; PNG is fine for screens.",
              },
            ]}
            whatToAvoid={[
              "Don't drop EC below 23% for boarding passes or laminated tickets — wear destroys low-EC symbols quickly.",
              "Don't force compact mode for a payload over ~110 characters — the encoder will reject it.",
              "Don't enable Reader Programming for normal data payloads — most scanners refuse to decode it as data.",
              "Don't print on phone screens with the brightness turned low; Aztec relies on contrast that auto-brightness sometimes kills.",
              "Don't expect every legacy scanner to read full-range Aztec at 32 layers — verify with the actual reader you're targeting.",
            ]}
            tips={[
              "Aztec doesn't need a quiet zone — but a thin white margin still helps screen scans through phone bezels.",
              "For airline / rail use, follow the IATA BCBP or UIC-918 field layout exactly; downstream readers reject anything off-spec.",
              "Export SVG and rasterise to PNG only for the final asset — vector means you can resize without quality loss.",
              "Round-trip-test by scanning the rendered Aztec with the actual scanner that will read it in production.",
              "If your scanner supports both, prefer Compact Aztec for ticket-size labels — it scans faster from short distances.",
            ]}
          />
          <PrivacyNote />
          <AztecSections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="data">Data to encode</Label>
              <Textarea
                id="data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                rows={5}
                spellCheck={false}
                autoComplete="off"
                className="font-mono text-sm"
                placeholder="Paste up to 3000 characters…"
              />
              <p className="text-xs text-muted-foreground">
                {data.length} / {MAX_CHARS} characters. Aztec encodes ASCII, digits and Latin-1
                binary.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Error correction</Label>
                <span className="text-xs text-muted-foreground">{eclevel}%</span>
              </div>
              <Slider
                min={5}
                max={95}
                step={1}
                value={[eclevel]}
                onValueChange={(v) => setEclevel(v[0])}
              />
              <p className="text-xs text-muted-foreground">
                Spec default is 23%. Higher = more tolerance to print damage at the cost of a bigger
                symbol.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="layers">Layers</Label>
                <Select value={layers} onValueChange={(v) => setLayers(v as LayersChoice)}>
                  <SelectTrigger id="layers">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="auto">Auto</SelectItem>
                    {Array.from({ length: compact ? 4 : 32 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} layer{n === 1 ? "" : "s"} ({moduleCount(n, compact)}×
                        {moduleCount(n, compact)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Module scale</Label>
                  <span className="text-xs text-muted-foreground">{moduleScale}x</span>
                </div>
                <Slider
                  min={1}
                  max={5}
                  step={1}
                  value={[moduleScale]}
                  onValueChange={(v) => setModuleScale(v[0])}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="compact" className="cursor-pointer">
                  Compact Aztec
                </Label>
                <p className="text-xs text-muted-foreground">
                  2-ring bullseye, 1–4 layers — smaller symbol for short payloads (~110 chars max).
                </p>
              </div>
              <Switch id="compact" checked={compact} onCheckedChange={setCompact} />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="readerinit" className="cursor-pointer">
                  Reader programming
                </Label>
                <p className="text-xs text-muted-foreground">
                  Sets the reader-init flag for scanner-config Aztec codes. Leave off for data.
                </p>
              </div>
              <Switch id="readerinit" checked={readerInit} onCheckedChange={setReaderInit} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border bg-white p-4">
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated Aztec code preview"
                />
              )}
            </div>

            {symbolSize && (
              <div className="text-xs text-muted-foreground">
                Symbol: {symbolSize.w}×{symbolSize.h} px at scale {moduleScale}x.
              </div>
            )}

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground">Encoded payload</div>
                  <code className="mt-1 block max-h-24 overflow-auto break-all text-xs">
                    {data || "—"}
                  </code>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={copyData} disabled={!data}>
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={!!error || !svgMarkup}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`Aztec · ${data.slice(0, 60) || "code"}`}
        filename={safeFilename(data)}
        svgMarkup={svgMarkup}
        defaultCaption={data}
        defaults={{
          layoutKind: "single",
          widthMm: 40,
          sheetPreset: "averyL7160",
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function moduleCount(layers: number, compact: boolean): number {
  // Compact Aztec layer N -> 11 + 4*N modules (15, 19, 23, 27).
  // Full-range Aztec layer N -> 15 + 4*N modules (19, 23, 27, 31, …).
  return compact ? 11 + 4 * layers : 15 + 4 * layers;
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        Aztec codes are rendered locally with bwip-js. The text you encode never touches our
        servers.
      </p>
    </>
  );
}

function AztecSections() {
  return (
    <>
      <h2>Aztec Code vs QR Code</h2>
      <p>An Aztec code is located from a bullseye at its center, while a QR code relies on three finder squares in its corners, so Aztec needs no quiet zone.</p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Aztec Code</th>
              <th scope="col">QR Code</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Quiet zone</td>
              <td>None required</td>
              <td>4 modules on every side</td>
            </tr>
            <tr>
              <td>Symbol sizes</td>
              <td>15 × 15 to 151 × 151 modules</td>
              <td>21 × 21 to 177 × 177 modules</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
      <p>Pick Aztec for tight spaces and phone screens, and QR for codes that shoppers scan with a camera app. The <Link to="/barcode-guides/barcode-formats">barcode formats guide</Link> compares Aztec with the other symbologies.</p>
      <h2>What Is an Aztec Code Used For?</h2>
      <p>Aztec codes appear mainly on travel tickets, and the symbology can also carry scanner configuration commands.</p>
      <h3>Aztec Barcodes on Boarding Passes and Rail Tickets</h3>
      <p>IATA's Bar Coded Boarding Pass standard allows Aztec on mobile boarding passes, and UIC 918-3 rail tickets use it too. The sample above follows the BCBP layout: M for the format code, then 1 for the number of flight legs. Before issuing a pass from this Aztec code generator online, scan it with the <Link to="/barcode-reader">barcode reader</Link> to confirm it decodes to the exact string you entered.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-reader",
      label: "Barcode Reader",
      desc: "Scan an Aztec code from your camera or an image.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode Formats Guide",
      desc: "How Aztec compares with the other 1D and 2D formats.",
    },
    {
      path: "/pdf417-generator",
      label: "PDF417 Generator",
      desc: "Stacked 2D barcode for IDs, manifests and licences.",
    },
    {
      path: "/qr-code-generator",
      label: "QR Code Generator",
      desc: "Consumer-friendly 2D barcode for phone scanners.",
    },
    {
      path: "/data-matrix-generator",
      label: "Data Matrix Generator",
      desc: "Compact 2D barcode for small parts and pharma.",
    },
    {
      path: "/barcode-guides/1d-vs-2d-barcodes",
      label: "1D vs 2D barcodes",
      desc: "When to use Aztec vs PDF417 vs QR vs Data Matrix.",
    },
    {
      path: "/barcode-type-detector",
      label: "Barcode Type Detector",
      desc: "Identify a barcode value's likely format.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/aztec-code-generator")({
  component: AztecCodeGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Aztec Code Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
